// ListManagementPage.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import {
  fetchLists,
  fetchListItems,
  createList,
  updateList,
  deleteList,
  removeItem,
  moveItem,
} from '../utils/listHandler';
import { useAppTokens } from '../context/ThemeModeContext';
import type { UserList, ListItem, ListOpenLevel } from '../types/rlTypes';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemMui from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import Popover from '@mui/material/Popover';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';

const openLevelLabel = (level: ListOpenLevel): string => {
  switch (level) {
    case 'public': return '公開';
    case 'url_share': return 'リンク共有';
    case 'friends': return '友達のみ';
    case 'private':
    default: return '非公開';
  }
};

const ListManagementPage: React.FC = () => {
  const [bookLists, setBookLists] = useState<UserList[]>([]);
  // 選択中リストのアイテム（遅延ロード + キャッシュ）
  const [itemsByList, setItemsByList] = useState<Record<string, ListItem[]>>({});
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { idToken } = useAuth();
  const navigate = useNavigate(); // eslint-disable-line @typescript-eslint/no-unused-vars

  const [editingList, setEditingList] = useState<UserList | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBook, setSelectedBook] = useState<number | null>(null);
  const [moveMenuAnchorEl, setMoveMenuAnchorEl] = useState<null | HTMLElement>(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingListId, setDeletingListId] = useState<string | null>(null);
  const [deletingListName, setDeletingListName] = useState<string>('');

  const [openCreateListDialog, setOpenCreateListDialog] = useState(false);
  const [newListName, setNewListName] = useState('気になる本');
  const [newListOpenLevel, setNewListOpenLevel] = useState<ListOpenLevel>('private');
  const [isCreatingList, setIsCreatingList] = useState(false);

  // ===== Style tokens (GraphPage準拠) =====
  const TOKENS = useAppTokens();

  const glassPanelSx = {
    background: TOKENS.glass,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 2,
    boxShadow: '0 0 0 rgba(0,0,0,0)',
    backdropFilter: 'blur(6px)',
  } as const;

  const ghostButtonSx = {
    color: TOKENS.text,
    background: TOKENS.buttonBg,
    border: `1px solid ${TOKENS.borderSoft}`,
    borderRadius: 999,
    textTransform: 'none',
    fontSize: 12,
    px: 1.25,
    py: 0.8,
    minHeight: 34,
    '&:hover': {
      background: TOKENS.buttonBgHover,
      border: `1px solid ${TOKENS.accent}`,
    },
    '&.Mui-disabled': {
      color: TOKENS.textSub,
      border: `1px solid ${TOKENS.borderSoft}`,
    },
  } as const;

  const iconButtonSx = {
    color: TOKENS.textSub,
    '&:hover': { color: TOKENS.text },
  } as const;

  const darkSurfacePaperSx = {
    backgroundColor: TOKENS.glassStrong,
    color: TOKENS.text,
    border: `1px solid ${TOKENS.border}`,
    backdropFilter: 'blur(6px)',
  } as const;

  const loadLists = useCallback(async (token: string) => {
    const res = await fetchLists(token);
    const sorted = (res.items ?? []).slice().sort(
      (a, b) => (a.user_list_order ?? 0) - (b.user_list_order ?? 0),
    );
    setBookLists(sorted);
    return sorted;
  }, []);

  const loadItemsForList = useCallback(async (token: string, listId: string) => {
    const res = await fetchListItems(listId, token);
    setItemsByList((prev) => ({ ...prev, [listId]: res.items ?? [] }));
    return res.items ?? [];
  }, []);

  const refreshAll = useCallback(async () => {
    if (!idToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const lists = await loadLists(idToken);
      if (lists.length > 0) {
        const firstId = lists[0].user_list_id;
        setSelectedList((cur) => cur ?? firstId);
        await loadItemsForList(idToken, selectedList ?? firstId);
      }
    } catch (e) {
      setError('リストデータの取得中にエラーが発生しました。');
    } finally {
      setIsLoading(false);
    }
  }, [idToken, loadLists, loadItemsForList, selectedList]);

  // 初回ロード
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!idToken) return;
      setIsLoading(true);
      setError(null);
      try {
        const lists = await loadLists(idToken);
        if (cancelled) return;
        if (lists.length > 0) {
          const firstId = lists[0].user_list_id;
          setSelectedList(firstId);
          await loadItemsForList(idToken, firstId);
        }
      } catch (e) {
        if (!cancelled) setError('リストデータの取得中にエラーが発生しました。');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [idToken, loadLists, loadItemsForList]);

  // 選択リスト切替時にアイテムを lazy ロード
  useEffect(() => {
    if (!idToken || !selectedList) return;
    if (itemsByList[selectedList]) return; // キャッシュあり
    loadItemsForList(idToken, selectedList).catch(() => {
      setSnackbarMessage('リストの本を取得できませんでした');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    });
  }, [idToken, selectedList, itemsByList, loadItemsForList]);

  const sortedSelectedListBooks = useMemo<ListItem[]>(() => {
    if (!selectedList) return [];
    const items = itemsByList[selectedList] ?? [];
    return items.slice().sort((a, b) => {
      const at = a.user_list_item_added_at ? new Date(a.user_list_item_added_at).getTime() : 0;
      const bt = b.user_list_item_added_at ? new Date(b.user_list_item_added_at).getTime() : 0;
      return bt - at;
    });
  }, [itemsByList, selectedList]);

  const handleListClick = (listId: string) => {
    setSelectedList(listId);
  };

  const handleBookClick = (rlBookId: number) => {
    window.open(`/book/${rlBookId}`, '_blank');
  };

  const handleEditList = (listId: string) => {
    const list = bookLists.find((l) => l.user_list_id === listId);
    if (list) {
      setEditingList(list);
      setIsEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    setEditingList(null);
  };

  const handleEditListSubmit = async () => {
    if (!editingList || !idToken) return;
    try {
      await updateList(
        editingList.user_list_id,
        {
          listName: editingList.user_list_name,
          description: editingList.user_list_description,
          openLevel: editingList.user_list_open_level,
        },
        idToken,
      );
      setSnackbarMessage('リストが更新されました');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      await loadLists(idToken);
    } catch (e) {
      setSnackbarMessage('リストの更新に失敗しました');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    handleEditDialogClose();
  };

  const handleShareList = (listId: string) => {
    const list = bookLists.find((l) => l.user_list_id === listId);
    if (list && (list.user_list_open_level === 'public' || list.user_list_open_level === 'url_share')) {
      const shareUrl = `https://readlinker.com/list/${listId}`;
      navigator.clipboard.writeText(shareUrl);
      setSnackbarMessage('共有URLがコピーされました');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }
  };

  const handleBookOptionsClick = (event: React.MouseEvent<HTMLButtonElement>, bookId: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedBook(bookId);
  };

  const handleBookOptionsClose = () => {
    setAnchorEl(null);
    setSelectedBook(null);
  };

  const handleRemoveFromList = async () => {
    if (!selectedBook || !selectedList || !idToken) return;
    try {
      await removeItem(selectedList, selectedBook, idToken);
      setSnackbarMessage('本がリストから削除されました');
      setSnackbarSeverity('success');
      // 選択中リストのアイテムキャッシュを再取得 + カウント更新用にリスト一覧も refresh
      await Promise.all([
        loadItemsForList(idToken, selectedList),
        loadLists(idToken),
      ]);
    } catch (e) {
      setSnackbarMessage('本の削除に失敗しました');
      setSnackbarSeverity('error');
    }
    setSnackbarOpen(true);
    handleBookOptionsClose();
  };

  const handleMoveToListClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoveMenuAnchorEl(event.currentTarget);
  };

  const handleMoveMenuClose = () => {
    setMoveMenuAnchorEl(null);
  };

  const handleMoveToList = async (targetListId: string) => {
    if (!selectedBook || !selectedList || !idToken) return;
    try {
      const { duplicated } = await moveItem(selectedList, selectedBook, targetListId, idToken);
      setSnackbarMessage(duplicated
        ? '移動先リストに重複があったため元から削除しました'
        : '別のリストに移動しました');
      setSnackbarSeverity('success');
      // 両方のリストのアイテムキャッシュを更新
      await Promise.all([
        loadItemsForList(idToken, selectedList),
        loadItemsForList(idToken, targetListId),
        loadLists(idToken),
      ]);
    } catch (e) {
      setSnackbarMessage('本の移動に失敗しました');
      setSnackbarSeverity('error');
    }
    setSnackbarOpen(true);
    handleBookOptionsClose();
    handleMoveMenuClose();
  };

  const handleDeleteListClick = (listId: string) => {
    const listToDelete = bookLists.find((l) => l.user_list_id === listId);
    if (listToDelete) {
      setDeletingListId(listId);
      setDeletingListName(listToDelete.user_list_name);
      setDeleteConfirmOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingListId || !idToken) return;
    try {
      await deleteList(deletingListId, idToken);
      setSnackbarMessage('リストを削除しました');
      setSnackbarSeverity('success');
      setItemsByList((prev) => {
        const next = { ...prev };
        delete next[deletingListId];
        return next;
      });
      if (selectedList === deletingListId) {
        setSelectedList(null);
      }
      await loadLists(idToken);
    } catch (e) {
      setSnackbarMessage('リストの削除に失敗しました');
      setSnackbarSeverity('error');
    }
    setSnackbarOpen(true);
    setDeleteConfirmOpen(false);
    setDeletingListId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeletingListId(null);
  };

  const handleCreateNewList = () => {
    setOpenCreateListDialog(true);
  };

  const handleCloseCreateListDialog = () => {
    setOpenCreateListDialog(false);
    setNewListName('気になる本');
    setNewListOpenLevel('private');
  };

  const handleCreateList = async () => {
    if (!idToken || !newListName.trim()) return;
    setIsCreatingList(true);
    try {
      const newList = await createList(newListName, null, newListOpenLevel, idToken);
      setSnackbarMessage('リストが作成されました');
      setSnackbarSeverity('success');
      await loadLists(idToken);
      if (newList?.user_list_id) {
        setSelectedList(newList.user_list_id);
      }
    } catch (e) {
      setSnackbarMessage('リストの作成に失敗しました');
      setSnackbarSeverity('error');
    } finally {
      setIsCreatingList(false);
      setSnackbarOpen(true);
      setOpenCreateListDialog(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _refreshAllUnused = refreshAll; // TODO: pull-to-refresh 等で使う想定で残す

  // ----- Render -----
  if (isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '100dvh',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          color: TOKENS.text,
        }}
      >
        <Box sx={{ ...glassPanelSx, p: 2, width: 'min(520px, 92vw)' }}>
          <Typography sx={{ fontSize: 13, color: TOKENS.textSub, mb: 1 }}>リストを読み込んでいます...</Typography>
          <LinearProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '100dvh',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          color: TOKENS.text,
        }}
      >
        <Box sx={{ ...glassPanelSx, p: 2 }}>
          <Typography sx={{ fontSize: 13, color: TOKENS.error }}>{error}</Typography>
        </Box>
      </Box>
    );
  }

  const selectedListObj = selectedList ? bookLists.find((l) => l.user_list_id === selectedList) : null;

  return (
    <Box sx={{ mt: 7 }}>
      <Box
        sx={{
          width: '100%',
          minHeight: 'calc(100dvh - 56px)',
          height: { xs: 'auto', md: 'calc(100vh - 56px)' },
          overflowY: { xs: 'auto', md: 'hidden' },
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          color: TOKENS.text,
          position: 'relative',
        }}
      >
        {/* page title */}
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            left: 14,
            zIndex: 5,
            display: 'flex',
            gap: 1.5,
            alignItems: 'baseline',
            flexWrap: 'wrap',
            pointerEvents: 'none',
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: TOKENS.text }}>リスト管理</Typography>
          <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
            {bookLists.length}件
            {selectedListObj?.user_list_name ? ` ・ ${selectedListObj.user_list_name}` : ''}
          </Typography>
        </Box>

        <Container
          maxWidth={false}
          sx={{
            height: { xs: 'auto', md: '100%' },
            py: 6,
            px: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              ...glassPanelSx,
              height: { xs: 'auto', md: 'calc(100vh - 48px)' },
              display: 'flex',
              flexDirection: 'column',
              overflow: { xs: 'visible', md: 'hidden' },
            }}
          >
            {/* header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${TOKENS.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>本のリストを作成・編集・共有できます</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={handleCreateNewList} sx={ghostButtonSx}>
                  ＋ リストを新規作成
                </Button>
              </Box>
            </Box>

            {/* main split */}
            {bookLists.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                  リストがありません。新しいリストを作成してください。
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2,
                  alignItems: 'stretch',
                  height: { xs: 'auto', md: '100%' },
                  minHeight: 0,
                  overflow: { xs: 'visible', md: 'hidden' },
                  p: 2,
                }}
              >
                {/* 左：リスト一覧 */}
                <Box
                  sx={{
                    width: { xs: '100%', md: 320 },
                    flexShrink: 0,
                    minHeight: 0,
                    overflow: { xs: 'visible', md: 'hidden' },
                    height: { xs: '50dvh', md: 'auto' },
                    borderRadius: 2,
                    border: `1px solid ${TOKENS.border}`,
                    background: TOKENS.cardBg,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderBottom: `1px solid ${TOKENS.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>リスト</Typography>
                    <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>{bookLists.length}</Typography>
                  </Box>

                  <Box
                    sx={{
                      overflowY: { xs: 'auto', md: 'auto' },
                      minHeight: 0,
                    }}
                  >
                    <List sx={{ py: 0 }}>
                      {bookLists.map((list) => (
                        <ListItemMui
                          key={list.user_list_id}
                          disablePadding
                          secondaryAction={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pointerEvents: 'auto' }}>
                              <IconButton
                                edge="end"
                                aria-label="edit"
                                onClick={() => handleEditList(list.user_list_id)}
                                sx={iconButtonSx}
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() => handleDeleteListClick(list.user_list_id)}
                                sx={iconButtonSx}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                edge="end"
                                aria-label="share"
                                onClick={() => handleShareList(list.user_list_id)}
                                disabled={list.user_list_open_level === 'private' || list.user_list_open_level === 'friends'}
                                sx={iconButtonSx}
                                size="small"
                              >
                                <ShareIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemButton
                            selected={selectedList === list.user_list_id}
                            onClick={() => handleListClick(list.user_list_id)}
                            sx={{
                              py: 1.1,
                              '&.Mui-selected': {
                                background: 'rgba(148,163,184,0.12)',
                              },
                              '&.Mui-selected:hover': {
                                background: 'rgba(148,163,184,0.16)',
                              },
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 6 }}>
                                  <Typography sx={{ fontSize: 13, color: TOKENS.text }}>
                                    {list.user_list_name}
                                  </Typography>
                                  {list.user_list_open_level === 'public' ? (
                                    <PublicIcon sx={{ fontSize: 13, color: TOKENS.textSub }} titleAccess="公開" />
                                  ) : (
                                    <LockIcon sx={{ fontSize: 13, color: TOKENS.textSub }} titleAccess="非公開" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 0.25 }}>
                                  <Typography component="span" sx={{ fontSize: 12, color: TOKENS.textSub }}>
                                    {`${list.item_count ?? 0} 冊`}
                                  </Typography>
                                  <Typography component="span" sx={{ fontSize: 12, color: TOKENS.textSub }}>
                                    {'  •  '}
                                    {openLevelLabel(list.user_list_open_level)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItemButton>
                        </ListItemMui>
                      ))}
                    </List>
                  </Box>
                </Box>

                {/* 右：本一覧 */}
                <Box
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflow: { xs: 'visible', md: 'hidden' },
                    borderRadius: 2,
                    border: `1px solid ${TOKENS.border}`,
                    background: TOKENS.cardBg,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderBottom: `1px solid ${TOKENS.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
                      {selectedListObj?.user_list_name || '本'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>最新追加順</Typography>
                      {selectedList ? (
                        <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>
                          {sortedSelectedListBooks.length}冊
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>

                  <Box sx={{ px: 1.5, pt: 1.25 }}>
                    {selectedListObj?.user_list_description ? (
                      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, whiteSpace: 'pre-wrap' }}>
                        {selectedListObj.user_list_description}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                        （説明なし）
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.5,
                      overflowY: { xs: 'visible', md: 'auto' },
                      minHeight: 0,
                    }}
                  >
                    {selectedList ? (
                      <Grid container spacing={1.5} columns={{ xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }}>
                        {sortedSelectedListBooks.map((book) => {
                          const cover = generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13);
                          return (
                            <Grid item xs={1} key={book.rl_book_id}>
                              <Card
                                sx={{
                                  transition: '0.25s',
                                  background: TOKENS.cardBg,
                                  border: `1px solid ${TOKENS.border}`,
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    border: `1px solid ${TOKENS.accent}`,
                                    background: TOKENS.cardBgHover,
                                  },
                                }}
                                onClick={() => handleBookClick(book.rl_book_id)}
                              >
                                <CardMedia
                                  component="img"
                                  sx={{
                                    height: 190,
                                    width: '100%',
                                    objectFit: 'contain',
                                    padding: '10px',
                                    background: TOKENS.cardBg,
                                  }}
                                  image={cover}
                                  alt={book.title ?? ''}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 0.5, py: 0.25 }}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleBookOptionsClick(e as React.MouseEvent<HTMLButtonElement>, book.rl_book_id)}
                                    sx={iconButtonSx}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Card>

                              <Box sx={{ mt: 0.75 }}>
                                <Typography
                                  sx={{
                                    fontSize: 12.5,
                                    color: TOKENS.text,
                                    lineHeight: 1.25,
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 2,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {book.title}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: TOKENS.textSub,
                                    mt: 0.25,
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 1,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {book.author}
                                </Typography>

                                <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.25 }}>
                                  {book.pub_year ? `${book.pub_year}` : ''}
                                </Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    ) : (
                      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>リストを選択してください。</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* Book options menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleBookOptionsClose}
            PaperProps={{ sx: darkSurfacePaperSx }}
          >
            <MenuItem onClick={handleRemoveFromList} sx={{ fontSize: 12.5, color: TOKENS.text }}>
              リストから削除
            </MenuItem>
            <MenuItem onClick={handleMoveToListClick} sx={{ fontSize: 12.5, color: TOKENS.text }}>
              他のリストへ移動
            </MenuItem>
          </Menu>

          {/* Move-to list popover */}
          <Popover
            open={Boolean(moveMenuAnchorEl)}
            anchorEl={moveMenuAnchorEl}
            onClose={handleMoveMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ sx: darkSurfacePaperSx }}
          >
            <List sx={{ py: 0 }}>
              {bookLists
                .filter((list) => list.user_list_id !== selectedList)
                .map((list) => (
                  <MenuItem
                    key={list.user_list_id}
                    onClick={() => handleMoveToList(list.user_list_id)}
                    sx={{ fontSize: 12.5, color: TOKENS.text }}
                  >
                    {list.user_list_name}
                  </MenuItem>
                ))}
            </List>
          </Popover>

          {/* Delete list confirm */}
          <Dialog
            open={deleteConfirmOpen}
            onClose={handleDeleteCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{ sx: { ...darkSurfacePaperSx, borderRadius: 2 } }}
          >
            <DialogTitle id="alert-dialog-title" sx={{ fontSize: 14 }}>
              リストの削除確認
            </DialogTitle>
            <DialogContent>
              <Typography id="alert-dialog-description" sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                {deletingListName} を削除してもよろしいですか？
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button onClick={handleDeleteCancel} sx={ghostButtonSx}>
                キャンセル
              </Button>
              <Button onClick={handleDeleteConfirm} sx={{ ...ghostButtonSx, borderColor: 'rgba(248,113,113,0.55)' }}>
                削除
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit list dialog */}
          <Dialog
            open={isEditDialogOpen}
            onClose={handleEditDialogClose}
            PaperProps={{ sx: { ...darkSurfacePaperSx, borderRadius: 2 } }}
          >
            <DialogTitle sx={{ fontSize: 14 }}>リストを編集</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <TextField
                autoFocus
                margin="dense"
                label="リスト名"
                type="text"
                fullWidth
                value={editingList?.user_list_name || ''}
                onChange={(e) => setEditingList((prev) => (prev ? { ...prev, user_list_name: e.target.value } : null))}
                InputLabelProps={{ sx: { color: TOKENS.textSub } }}
                InputProps={{ sx: { color: TOKENS.text } }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.border },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                }}
              />
              <TextField
                margin="dense"
                label="説明"
                type="text"
                fullWidth
                multiline
                rows={4}
                value={editingList?.user_list_description || ''}
                onChange={(e) => setEditingList((prev) => (prev ? { ...prev, user_list_description: e.target.value } : null))}
                InputLabelProps={{ sx: { color: TOKENS.textSub } }}
                InputProps={{ sx: { color: TOKENS.text } }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.border },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                }}
              />
              <FormControl fullWidth margin="dense">
                <InputLabel sx={{ color: TOKENS.textSub }}>公開設定</InputLabel>
                <Select
                  value={editingList?.user_list_open_level || 'private'}
                  label="公開設定"
                  onChange={(e) => setEditingList((prev) => (prev ? {
                    ...prev,
                    user_list_open_level: e.target.value as ListOpenLevel,
                  } : null))}
                  sx={{
                    color: TOKENS.text,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.border },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                    '& .MuiSvgIcon-root': { color: TOKENS.textSub },
                  }}
                  MenuProps={{ PaperProps: { sx: darkSurfacePaperSx } }}
                >
                  <MenuItem value="public" sx={{ fontSize: 12.5 }}>公開</MenuItem>
                  <MenuItem value="private" sx={{ fontSize: 12.5 }}>非公開</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button onClick={handleEditDialogClose} sx={ghostButtonSx}>キャンセル</Button>
              <Button onClick={handleEditListSubmit} sx={ghostButtonSx}>保存</Button>
            </DialogActions>
          </Dialog>

          {/* Create list dialog */}
          <Dialog
            open={openCreateListDialog}
            onClose={handleCloseCreateListDialog}
            PaperProps={{ sx: { ...darkSurfacePaperSx, borderRadius: 2 } }}
          >
            <DialogTitle sx={{ fontSize: 14 }}>新しいリストを作成</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <TextField
                autoFocus
                margin="dense"
                id="name"
                label="リスト名"
                type="text"
                fullWidth
                variant="outlined"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                InputLabelProps={{ sx: { color: TOKENS.textSub } }}
                InputProps={{ sx: { color: TOKENS.text } }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.border },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                }}
              />
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="open-level-label" sx={{ color: TOKENS.textSub }}>
                  公開範囲
                </InputLabel>
                <Select
                  labelId="open-level-label"
                  id="open-level"
                  value={newListOpenLevel}
                  label="公開範囲"
                  onChange={(e) => setNewListOpenLevel(e.target.value as ListOpenLevel)}
                  sx={{
                    color: TOKENS.text,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.border },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                    '& .MuiSvgIcon-root': { color: TOKENS.textSub },
                  }}
                  MenuProps={{ PaperProps: { sx: darkSurfacePaperSx } }}
                >
                  <MenuItem value="public" sx={{ fontSize: 12.5 }}>公開</MenuItem>
                  <MenuItem value="private" sx={{ fontSize: 12.5 }}>非公開</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button onClick={handleCloseCreateListDialog} disabled={isCreatingList} sx={ghostButtonSx}>
                キャンセル
              </Button>
              <Button
                onClick={handleCreateList}
                disabled={isCreatingList || !newListName.trim()}
                sx={ghostButtonSx}
              >
                {isCreatingList ? '作成中...' : '作成'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              sx={{
                width: '100%',
                background: TOKENS.glassStrong,
                color: TOKENS.text,
                border: `1px solid ${TOKENS.border}`,
              }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </Box>
  );
};

export default ListManagementPage;
